import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useForm } from 'react-typed-form';
import { FieldSelect } from '../../form';
import { toast } from 'sonner';
import { Api, useApiCallable } from '../../api';
import { toastErrorHandler } from '../../util';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  format,
  parseISO,
  isMonday,
  nextMonday as dfNextMonday,
} from 'date-fns';

interface NewTeamModalProps {
  open: boolean;
  onClose: () => void;
  organisationName: string;
  organisationId: string;
  existingTeam?: any | null;
  onSave: () => void;
}

export default function NewTeamModal({
  open,
  onClose,
  organisationName,
  organisationId,
  existingTeam,
  onSave,
}: NewTeamModalProps) {
  const coachesApi = Api.User.useReadCollectionClinicians({
    'clinicianProfile.type': 'coach',
    ['pagination' as any]: false,
  });

  const coachOptions = coachesApi.data?.member
    ? [
        { label: 'Select', value: '' },
        ...coachesApi.data.member.map((coach) => ({
          label: coach.name || coach.email,
          value: coach['@id'],
        })),
      ]
    : [{ label: 'Loading...', value: '' }];

  const callApi = useApiCallable();
  const programHasStarted =
    existingTeam && new Date(existingTeam.programStartDate) <= new Date();

  const minDate = dfNextMonday(new Date());
  const form = useForm<{
    coach: string;
    programStartDate: string;
    organisation: string;
  }>({
    defaultValues: {
      coach: existingTeam?.coach['@id'] || '',
      programStartDate:
        existingTeam?.programStartDate || format(minDate, 'yyyy-MM-dd'),
      organisation: `/organisations/${organisationId}`,
    },
    onSubmit: async (values) => {
      let data: Partial<Api.OrganisationTeam.W> = {
        coach: values.coach,
      };

      if (!existingTeam) {
        data = {
          ...data,
          organisation: values.organisation,
        };
      }

      if (!programHasStarted) {
        data = {
          ...data,
          programStartDate: values.programStartDate,
        };
      }

      const res = await callApi(
        existingTeam
          ? `/organisation-teams/${existingTeam.id}`
          : '/organisation-teams',
        {
          method: existingTeam ? 'PATCH' : 'POST',
          jsonBody: data,
        },
        { errorHandler: toastErrorHandler },
      );

      if (res) {
        toast.success(existingTeam ? 'Team updated' : 'Team created');
        onClose();
        onSave();
      }
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack>
            <Typography variant="h6">
              {existingTeam
                ? 'Edit team'
                : `Create a new team for ${organisationName}`}
            </Typography>
            {!existingTeam && (
              <Typography variant="body2" color="text.secondary">
                You will be able to create licenses after creating this team
              </Typography>
            )}
          </Stack>
          <IconButton onClick={handleClose}>
            <X size={24} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <FieldSelect
              field={form.getField('coach')}
              label="Coach"
              options={coachOptions}
              required
            />
          </Box>

          <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Program Start Date"
                value={(() => {
                  const v = form.getField('programStartDate').value;
                  return v ? parseISO(v) : null;
                })()}
                onChange={(date) => {
                  if (!date) {
                    form.getField('programStartDate').handleValueChange('');
                    return;
                  }
                  form
                    .getField('programStartDate')
                    .handleValueChange(format(date, 'yyyy-MM-dd'));
                }}
                minDate={minDate}
                shouldDisableDate={(day) => !isMonday(day)}
                format="dd/MM/yyyy"
                disabled={programHasStarted}
                slotProps={{
                  textField: {
                    error: false,
                    helperText: programHasStarted
                      ? 'Cannot change start date after program has started'
                      : undefined,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={form.handleSubmit} variant="contained">
          {existingTeam ? 'Save Changes' : 'Create Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
